// Updated: 2026-08-20 10:15:00 CDT
// Purpose: Preserve screenplay source identity while building complete, speech-ready generation sequences.

import type { Character, SequenceItem } from '@/types/screenplay';
import { prepareTextForSpeech } from '@/utils/speechText';

export interface BulletproofAudioBlock {
  id: string;
  sourceBlockId: string;
  sourceType: SequenceItem['type'];
  type: 'dialogue' | 'action' | 'parenthetical';
  character: string;
  sourceText: string;
  text: string;
  originalOrder: number;
  voiceId: string;
  intonation?: SequenceItem['intonation'];
  speechLanguage?: SequenceItem['speechLanguage'];
  debugInfo: {
    elementIndex: number;
    wasSkipped: boolean;
    assignmentReason: string;
  };
}

export interface BulletproofSequenceMap {
  audioBlocks: BulletproofAudioBlock[];
  voiceAssignments: { [character: string]: string };
  totalBlocks: number;
  processingLog: string[];
  validationErrors: string[];
}

export class BulletproofSequencer {
  
  /**
   * Create audio blocks with ZERO tolerance for skipping content
   */
  static createBulletproofSequence(
    characters: Character[], 
    voiceAssignments: { [character: string]: string }
  ): BulletproofSequenceMap {
    console.log("\n🛡️ BULLETPROOF SEQUENCER STARTING");
    
    const processingLog: string[] = [];
    const validationErrors: string[] = [];
    const audioBlocks: BulletproofAudioBlock[] = [];
    
    processingLog.push("=== BULLETPROOF AUDIO SEQUENCING ===");
    processingLog.push(`Voice assignments: ${Object.keys(voiceAssignments).join(', ')}`);
    
    // Find screenplay sequence
    const sequenceData = characters.find(c => c.name === "SCREENPLAY_SEQUENCE");
    if (!sequenceData || !sequenceData.sequence) {
      const error = "CRITICAL: No screenplay sequence data found";
      validationErrors.push(error);
      throw new Error(error);
    }

    processingLog.push(`Processing ${sequenceData.sequence.length} sequence elements`);

    // Process every spoken element; parentheticals are delivery metadata and are intentionally silent.
    sequenceData.sequence.forEach((item, index) => {
      let targetCharacter: string;
      let blockType: 'dialogue' | 'action' | 'parenthetical';
      let assignmentReason: string;
      let wasSkipped = false;

      if (item.type === 'parenthetical') {
        processingLog.push(`Element ${index}: Parenthetical retained as silent delivery metadata`);
        return;
      }

      // Determine character and type
      if (item.type === 'dialogue' && item.character) {
        targetCharacter = item.character;
        blockType = 'dialogue';
        assignmentReason = `Dialogue line for character ${item.character}`;
        
      } else if (item.type === 'action') {
        targetCharacter = 'SCENE NARRATOR';
        blockType = 'action';
        assignmentReason = "Action description assigned to SCENE NARRATOR";
        
      } else {
        // Fallback - assign to SCENE NARRATOR rather than skip
        targetCharacter = 'SCENE NARRATOR';
        blockType = 'action';
        assignmentReason = `Unknown type '${item.type}' assigned to SCENE NARRATOR as fallback`;
        processingLog.push(`⚠️  Element ${index}: Unknown type '${item.type}', assigning to SCENE NARRATOR`);
      }

      // Check voice assignment
      const voiceId = voiceAssignments[targetCharacter];
      if (!voiceId) {
        const errorMsg = `Missing voice for '${targetCharacter}' - ELEMENT WILL BE SKIPPED: "${item.text.substring(0, 50)}..."`;
        validationErrors.push(errorMsg);
        processingLog.push(`❌ ${errorMsg}`);
        wasSkipped = true;
        return; // This is the only acceptable skip
      }

      // Create bulletproof audio block
      const audioBlock: BulletproofAudioBlock = {
        id: item.id?.trim() || `bulletproof_${String(index).padStart(4, '0')}_${targetCharacter.replace(/\s+/g, '_').toLowerCase()}`,
        sourceBlockId: item.id?.trim() || `sequence-${item.order}-${index}`,
        sourceType: item.type,
        type: blockType,
        character: targetCharacter,
        sourceText: item.text,
        text: prepareTextForSpeech(item.text, item.type),
        originalOrder: item.order,
        voiceId: voiceId,
        intonation: item.intonation,
        ...(item.speechLanguage !== undefined ? { speechLanguage: item.speechLanguage } : {}),
        debugInfo: {
          elementIndex: index,
          wasSkipped,
          assignmentReason
        }
      };

      audioBlocks.push(audioBlock);
      
      processingLog.push(`✅ Block ${audioBlock.id}: [${blockType.toUpperCase()}] ${targetCharacter} - "${item.text.substring(0, 40)}..."`);
      processingLog.push(`   Reason: ${assignmentReason}`);
    });

    // Final validation
    if (audioBlocks.length === 0) {
      const error = "CRITICAL: No audio blocks created - all content was skipped";
      validationErrors.push(error);
      throw new Error(error);
    }

    // Sort by original order to ensure correct sequence
    audioBlocks.sort((a, b) => a.originalOrder - b.originalOrder);
    
    processingLog.push(`\n📊 FINAL STATS:`);
    processingLog.push(`  Total blocks created: ${audioBlocks.length}`);
    processingLog.push(`  Validation errors: ${validationErrors.length}`);
    processingLog.push(`  Voice assignments used: ${Object.keys(voiceAssignments).length}`);

    // Count by character
    const characterCounts: { [char: string]: number } = {};
    audioBlocks.forEach(block => {
      characterCounts[block.character] = (characterCounts[block.character] || 0) + 1;
    });
    
    Object.entries(characterCounts).forEach(([char, count]) => {
      processingLog.push(`  ${char}: ${count} blocks`);
    });

    console.log(`🛡️ Bulletproof sequencer complete: ${audioBlocks.length} blocks created`);
    
    return {
      audioBlocks,
      voiceAssignments,
      totalBlocks: audioBlocks.length,
      processingLog,
      validationErrors
    };
  }

  /**
   * Convert to edge function format
   */
  static toEdgeFunctionFormat(sequenceMap: BulletproofSequenceMap) {
    // Re-validate order based on originalOrder
    const sortedBlocks = [...sequenceMap.audioBlocks].sort((a, b) => a.originalOrder - b.originalOrder);
    
    const audioBlocks = sortedBlocks.map((block, index) => ({
      id: block.id,
      type: block.type === 'dialogue' ? 'dialogue' : 'action',
      character: block.character,
      sourceText: block.sourceText,
      text: block.text,
      originalOrder: block.originalOrder,
      voiceId: block.voiceId,
      sequenceIndex: index // Add explicit sequence index
    }));

    const voiceAssignments = Object.entries(sequenceMap.voiceAssignments).map(([character, voiceId]) => ({
      character,
      voiceId
    }));

    return {
      audioBlocks,
      voiceAssignments,
      totalBlocks: audioBlocks.length,
      debugInfo: {
        processingLog: sequenceMap.processingLog,
        validationErrors: sequenceMap.validationErrors
      }
    };
  }

  /**
   * Validate sequence integrity
   */
  static validateSequence(sequenceMap: BulletproofSequenceMap): {
    isValid: boolean;
    errors: string[];
    warnings: string[];
    stats: {
      totalBlocks: number;
      charactersUsed: string[];
      orderIntegrity: boolean;
    };
  } {
    const errors: string[] = [];
    const warnings: string[] = [];

    // Check for empty sequence
    if (sequenceMap.audioBlocks.length === 0) {
      errors.push("No audio blocks found - sequence is empty");
    }

    // Check voice assignments
    const charactersUsed = new Set(sequenceMap.audioBlocks.map(b => b.character));
    charactersUsed.forEach(char => {
      if (!sequenceMap.voiceAssignments[char]) {
        errors.push(`Missing voice assignment for character: ${char}`);
      }
    });

    // Check order integrity
    let orderIntegrity = true;
    for (let i = 1; i < sequenceMap.audioBlocks.length; i++) {
      if (sequenceMap.audioBlocks[i].originalOrder < sequenceMap.audioBlocks[i-1].originalOrder) {
        orderIntegrity = false;
        warnings.push(`Order inconsistency at block ${i}: ${sequenceMap.audioBlocks[i].originalOrder} < ${sequenceMap.audioBlocks[i-1].originalOrder}`);
      }
    }

    // Check for duplicate IDs
    const ids = new Set();
    sequenceMap.audioBlocks.forEach(block => {
      if (ids.has(block.id)) {
        errors.push(`Duplicate block ID: ${block.id}`);
      }
      ids.add(block.id);
    });

    return {
      isValid: errors.length === 0,
      errors,
      warnings,
      stats: {
        totalBlocks: sequenceMap.audioBlocks.length,
        charactersUsed: Array.from(charactersUsed),
        orderIntegrity
      }
    };
  }

  /**
   * Get preview for UI
   */
  static getPreview(sequenceMap: BulletproofSequenceMap): Array<{
    id: string;
    character: string;
    text: string;
    type: string;
    hasVoice: boolean;
    debugReason: string;
  }> {
    return sequenceMap.audioBlocks.slice(0, 15).map(block => ({
      id: block.id,
      character: block.character,
      text: block.text,
      type: block.type,
      hasVoice: !!block.voiceId,
      debugReason: block.debugInfo.assignmentReason
    }));
  }
}
